import { useEffect, useState, useRef, RefObject, useCallback } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

interface UseKeyboardHandlerOptions {
  elementRef?: RefObject<HTMLElement>;
  scrollIntoView?: boolean;
  scrollDelay?: number;
  scrollBehavior?: ScrollBehavior;
  scrollBlock?: ScrollLogicalPosition;
  onKeyboardShow?: (info: KeyboardInfo) => void;
  onKeyboardHide?: () => void;
}

interface KeyboardInfo {
  keyboardHeight: number;
}

export const useKeyboardHandler = (options: UseKeyboardHandlerOptions = {}) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const keyboardListenersRef = useRef<{ show?: any; hide?: any }>({});
  
  const {
    elementRef,
    scrollIntoView = true,
    scrollDelay = 300,
    scrollBehavior = 'smooth',
    scrollBlock = 'center',
    onKeyboardShow,
    onKeyboardHide
  } = options;

  // Check if we're on a native platform
  useEffect(() => {
    setIsNativePlatform(Capacitor.isNativePlatform());
  }, []);

  // Scroll element into view with better error handling
  const scrollElementIntoView = useCallback(() => {
    if (!scrollIntoView || !elementRef?.current) return;

    try {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        elementRef.current?.scrollIntoView({
          behavior: scrollBehavior,
          block: scrollBlock,
        });
      });
    } catch (error) {
      console.warn('Error scrolling element into view:', error);
    }
  }, [elementRef, scrollIntoView, scrollBehavior, scrollBlock]);

  // Handle keyboard show event
  const handleKeyboardShow = useCallback((info: KeyboardInfo) => {
    setIsKeyboardOpen(true);
    setKeyboardHeight(info.keyboardHeight);
    
    // Call custom callback if provided
    onKeyboardShow?.(info);
    
    // Scroll element into view with delay
    if (scrollIntoView) {
      setTimeout(scrollElementIntoView, scrollDelay);
    }
  }, [onKeyboardShow, scrollIntoView, scrollElementIntoView, scrollDelay]);

  // Handle keyboard hide event
  const handleKeyboardHide = useCallback(() => {
    setIsKeyboardOpen(false);
    setKeyboardHeight(0);
    
    // Call custom callback if provided
    onKeyboardHide?.();
  }, [onKeyboardHide]);

  useEffect(() => {
    // Only add listeners on native platforms
    if (!isNativePlatform) {
      return;
    }

    const setupKeyboardListeners = async () => {
      try {
        // Check if Keyboard plugin is available
        if (!Capacitor.isPluginAvailable('Keyboard')) {
          console.warn('Keyboard plugin is not available');
          return;
        }

        // Clean up existing listeners
        if (keyboardListenersRef.current.show) {
          keyboardListenersRef.current.show.remove();
        }
        if (keyboardListenersRef.current.hide) {
          keyboardListenersRef.current.hide.remove();
        }

        // Listen for keyboard show events
        keyboardListenersRef.current.show = await Keyboard.addListener('keyboardWillShow', handleKeyboardShow);

        // Listen for keyboard hide events
        keyboardListenersRef.current.hide = await Keyboard.addListener('keyboardWillHide', handleKeyboardHide);

      } catch (error) {
        console.error('Error setting up keyboard listeners:', error);
      }
    };

    setupKeyboardListeners();

    // Cleanup listeners on unmount
    return () => {
      if (keyboardListenersRef.current.show) {
        keyboardListenersRef.current.show.remove();
      }
      if (keyboardListenersRef.current.hide) {
        keyboardListenersRef.current.hide.remove();
      }
    };
  }, [isNativePlatform, handleKeyboardShow, handleKeyboardHide]);

  // Function to manually hide keyboard
  const hideKeyboard = useCallback(async () => {
    try {
      if (isNativePlatform && Capacitor.isPluginAvailable('Keyboard')) {
        await Keyboard.hide();
      }
    } catch (error) {
      console.error('Error hiding keyboard:', error);
    }
  }, [isNativePlatform]);

  // Function to manually show keyboard (if supported)
  const showKeyboard = useCallback(async () => {
    try {
      if (isNativePlatform && Capacitor.isPluginAvailable('Keyboard')) {
        await Keyboard.show();
      }
    } catch (error) {
      console.error('Error showing keyboard:', error);
    }
  }, [isNativePlatform]);

  // Utility function to scroll element into view manually
  const scrollIntoViewManually = useCallback(() => {
    scrollElementIntoView();
  }, [scrollElementIntoView]);

  return {
    isKeyboardOpen,
    keyboardHeight,
    isNativePlatform,
    hideKeyboard,
    showKeyboard,
    scrollIntoViewManually,
  };
}; 